import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {doesPublicFileExistAsync} from '@Utils/file.util';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {RemoveVideoBackgroundTrimData} from '@Utils/video.util';
import {getEstimatedTimeForBackgroundRemoval, pollVideoBackgroundRemovalStatus, removeUserVideoBackground, removeVideoBackground} from '@Utils/video.util';
import type {TrimData} from '@Panels/trim-panels/trim-panels.types';
import {getRemovedBgFilenameWithExtension, getRepoVideoURL} from '@Libraries/user-video-library';
import {ItemLoadingProgressType, ItemLoadingReason} from '@PosterWhiteboard/items/item/item.types';
import {secondsToMilliseconds} from '@Utils/math.util';
import {isPremiumUser} from '@Utils/user.util';
import {startItemLoading, removeItemLoading} from '@Libraries/item-loading.library';

const ESTIMATED_OVERHEAD_TIME = 180;

export interface RemoveVideoItemBackgroundObject {
  isBackgroundRemoved: boolean;
  isBackgroundBeingRemoved: boolean;
  trimData: TrimData;
  pollingKey?: string;
}

export class RemoveVideoItemBackground extends CommonMethods {
  public isBackgroundRemoved = false;
  public isBackgroundBeingRemoved = false;
  public isBackgroundRemovalPollingInitialized = false;
  public trimData: TrimData = {
    startTime: 0,
    endTime: 0,
  };
  public pollingKey?: string;

  private item: VideoItem | VideoSlideItem;

  constructor(item: VideoItem | VideoSlideItem) {
    super();
    this.item = item;
  }

  public toObject(): RemoveVideoItemBackgroundObject {
    return {
      isBackgroundRemoved: this.isBackgroundRemoved,
      isBackgroundBeingRemoved: this.isBackgroundBeingRemoved,
      trimData: this.trimData,
      pollingKey: this.pollingKey,
    };
  }

  public clearInProgressState(): void {
    this.isBackgroundBeingRemoved = false;
    this.isBackgroundRemovalPollingInitialized = false;
    this.pollingKey = undefined;
  }

  public async ensureRemovedBackgroundVideoExistsIfNeeded(): Promise<void> {
    if (this.isBackgroundRemoved && !(await this.doesRemovedBackgroundFileExist())) {
      await removeVideoBackground(this.item.hashedFilename, this.item.removeBackground.trimData.startTime, this.item.removeBackground.trimData.endTime);
    }
  }

  public async doesRemovedBackgroundFileExist(): Promise<boolean> {
    const removedBgFilename = getRemovedBgFilenameWithExtension(
      this.item.hashedFilename,
      this.item.removeBackground.trimData.startTime,
      this.item.removeBackground.trimData.endTime
    );
    const destURL = getRepoVideoURL(removedBgFilename, this.item.page.poster.mode.isGeneration());
    return doesPublicFileExistAsync(destURL);
  }

  public async removeBackground(): Promise<void> {
    if (this.isBackgroundBeingRemoved) {
      return;
    }

    this.isBackgroundBeingRemoved = true;
    this.isBackgroundRemovalPollingInitialized = true;
    this.item.page.poster.redux.updateReduxData();
    try {
      const estimatedBackgroundRemovalTime = await getEstimatedTimeForBackgroundRemoval(
        this.item.hashedFilename,
        this.item.getStartTimeForNonRemovedBgVideo(),
        this.item.getEndTimeForNonRemovedBgVideo()
      );
      startItemLoading(this.item, {
        reason: ItemLoadingReason.AI_REMOVE_BACKGROUND,
        langTextKeys: ['pmwjs_removing_background_percentage', 'pmwjs_removing_bg_percentage'],
        progress: {
          type: ItemLoadingProgressType.FAKE,
          expectedTotalTime: estimatedBackgroundRemovalTime,
        },
      });

      const creditsToCharge: number = await this.getCreditsToChargeForBackgroundRemovalForUser();
      if ((await this.doesRemovedBackgroundFileExist()) && creditsToCharge === 0) {
        if (this.isBackgroundRemoved) {
          return;
        }

        const trimData: RemoveVideoBackgroundTrimData = {trimStart: this.item.startTime, trimEnd: this.item.endTime};
        await this.item.enableRemoveBackgroundFlag(trimData);
        return;
      }

      this.pollingKey = await removeUserVideoBackground(this.item.hashedFilename, this.item.startTime, this.item.endTime);
      const estimatedAjaxTimeoutInMilliseconds = secondsToMilliseconds(estimatedBackgroundRemovalTime + ESTIMATED_OVERHEAD_TIME);
      const response = await pollVideoBackgroundRemovalStatus(this.pollingKey, estimatedAjaxTimeoutInMilliseconds);

      if (!this.item.existsInPage()) {
        return;
      }

      if (response.remainingRemoveBackgroundCredits !== undefined) {
        getPosterUi().updateVideoBgRemovalCredits(this.item.hashedFilename, this.item.startTime, this.item.endTime, 0);
        void getPosterUi().refreshTeamCreditsInStore();

        await this.item.enableRemoveBackgroundFlag(response.trimData);
      }
    } catch (e: any) {
      this.onRemoveBackgroundError(e);
    } finally {
      this.resetRemoveBackgroundState();
    }
  }

  public async getCreditsToChargeForBackgroundRemovalForUser(): Promise<number> {
    return getPosterUi().getCreditsCostForVideoBackgroundRemoval(this.item.hashedFilename, this.item.getStartTimeForNonRemovedBgVideo(), this.item.getEndTimeForNonRemovedBgVideo());
  }

  private onInSufficientFunds(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const cb = (credits: number): void => {
        getPosterUi().onCreditsPurchaseSuccess(this.item, credits);
        resolve();
      };

      try {
        const creditsToCharge: number = await this.getCreditsToChargeForBackgroundRemovalForUser();
        const remainingCredits = window.PMW.redux.store.getState().teamCredits.credits;

        getPosterUi().triggerBuyMoreCredits({
          headingText: window.i18next.t('pmwjs_credits_pack_on_background_removal', {creditsRemaining: remainingCredits, creditsCost: creditsToCharge}),
          modalTitle: window.i18next.t('pmwjs_youre_out_of_credits'),
          cb,
        });
      } catch (e) {
        console.error(e);
        reject();
      }
    });
  }

  public async resumeRemoveBackground(): Promise<void> {
    if (!this.pollingKey) {
      return;
    }

    try {
      const estimatedBackgroundRemovalTime = await getEstimatedTimeForBackgroundRemoval(
        this.item.hashedFilename,
        this.item.getStartTimeForNonRemovedBgVideo(),
        this.item.getEndTimeForNonRemovedBgVideo()
      );
      const estimatedAjaxTimeoutInMilliseconds = secondsToMilliseconds(estimatedBackgroundRemovalTime + ESTIMATED_OVERHEAD_TIME);
      const response = await pollVideoBackgroundRemovalStatus(this.pollingKey, estimatedAjaxTimeoutInMilliseconds);

      if (!this.item.existsInPage()) {
        return;
      }

      if (response.remainingRemoveBackgroundCredits !== undefined) {
        getPosterUi().updateVideoBgRemovalCredits(this.item.hashedFilename, this.item.startTime, this.item.endTime, 0);
        void getPosterUi().refreshTeamCreditsInStore();

        await this.item.enableRemoveBackgroundFlag(response.trimData);
      }
    } catch (e: any) {
      this.onRemoveBackgroundError(e);
    } finally {
      this.resetRemoveBackgroundState();
    }
  }

  public resumeBackgroundRemovalIfNeeded(): void {
    if (this.isBackgroundBeingRemoved && !this.isBackgroundRemoved && !this.isBackgroundRemovalPollingInitialized) {
      this.isBackgroundRemovalPollingInitialized = true;
      void this.resumeRemoveBackground();
    }
  }

  private onRemoveBackgroundError(e: any): void {
    if (!this.item.existsInPage()) {
      return;
    }

    console.error(e);

    if (e.code === 'insufficient_credits') {
      if (!isPremiumUser()) {
        void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS);
      } else {
        void this.onInSufficientFunds();
      }
      return;
    }

    getPosterUi().openMessageModal({
      title: window.i18next.t('pmwjs_remove_video_bg_failed'),
      text: window.i18next.t('pmwjs_remove_video_bg_failed_desc'),
    });
  }

  private resetRemoveBackgroundState(): void {
    if (!this.item.existsInPage()) {
      return;
    }

    this.clearInProgressState();
    removeItemLoading(this.item);
    this.item.page.poster.redux.updateReduxData();
  }
}
